// src/components/operationCenter/widgets/TimeSeriesChartWidget.tsx
// Multi-token time series chart widget - follows CompareTab pattern

import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import type { WidgetProps, TimeSeriesChartConfig } from "@/types/dashboard";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useTokensHistory } from "@/hooks/tokens/compare/useCompareData";
import { useProcessedData } from "@/hooks/tokens/compare/useProcessedData";
import { 
  extractFromAlignedSeries, 
  indexAllSeries, 
  canIndexMetric,
  inferYAxisFormat,
} from "@/utils/charts/tokenSeries";
import { MetricKeys } from "@/constants/metrics";
import MultiTokenChart from "@/components/charts/composed/MultiTokenChart";
import { TimeRangePicker } from "@/components/common";
import CompactTokenSelector from "./shared/CompactTokenSelector";
import type { RangeKey } from "@/utils/tokens/dataProcessing";
import { WIDGET_EMPTY } from "../WidgetWrapper";

interface MetricOption {
  key: string;
  label: string;
}

const METRIC_OPTIONS: MetricOption[] = [
  { key: MetricKeys.PriceClose, label: "Price" },
  { key: MetricKeys.MarketCap, label: "Market Cap" },
  { key: MetricKeys.FDV, label: "FDV" },
  { key: MetricKeys.Volume, label: "Volume" },
];

const TimeSeriesChartWidget: React.FC<WidgetProps<TimeSeriesChartConfig>> = ({ 
  widget, 
  onConfigChange 
}) => {
  const config = widget.config as TimeSeriesChartConfig;
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(300);
  
  // State - initialized from config
  const [selectedTickers, setSelectedTickers] = useState<string[]>(config.tickers || ["BTC", "ETH"]);
  const [selectedMetric, setSelectedMetric] = useState(config.metric || MetricKeys.PriceClose);
  const [timeRange, setTimeRange] = useState<RangeKey>((config.timeframe as RangeKey) || "365d");
  const [indexTo100, setIndexTo100] = useState(config.index_to_100 ?? true);

  // Measure container height
  useEffect(() => {
    const updateHeight = () => {
      if (chartContainerRef.current) {
        const height = chartContainerRef.current.clientHeight;
        if (height > 0) {
          setChartHeight(height);
        }
      }
    };

    updateHeight();
    
    const resizeObserver = new ResizeObserver(updateHeight);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Data - using same hooks as CompareTab
  const { data: rows } = useTokensScreenerData();
  const historyQuery = useTokensHistory(selectedTickers, selectedMetric, selectedTickers.length > 0);
  
  const { alignedSeries, hasData, isLoading, error } = useProcessedData(
    historyQuery.data,
    selectedTickers,
    timeRange,
    selectedMetric,
    historyQuery.isLoading,
    historyQuery.error ?? null
  );

  // Available tokens from screener
  const availableTokens = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map(r => String(r["tokenSymbol"] ?? "").toUpperCase())
      .filter(Boolean)
      .slice(0, 100);
  }, [rows]);

  // Chart configuration - same logic as CompareTab
  const canIndex = useMemo(() => canIndexMetric(selectedMetric), [selectedMetric]);
  
  const { chartDates, chartSeries, yAxisFormat } = useMemo(() => {
    const { dates, series } = extractFromAlignedSeries(alignedSeries, selectedTickers);
    
    // Apply index to 100 if enabled and applicable
    let processedSeries = series;
    if (canIndex && indexTo100) {
      processedSeries = indexAllSeries(processedSeries, 100);
    }
    
    // Determine y-axis format
    const format = canIndex && indexTo100 ? 'number' : inferYAxisFormat(selectedMetric);
    
    return {
      chartDates: dates,
      chartSeries: processedSeries,
      yAxisFormat: format,
    };
  }, [alignedSeries, selectedTickers, indexTo100, canIndex, selectedMetric]);

  const metricLabel = useMemo(() => {
    return METRIC_OPTIONS.find((m) => m.key === selectedMetric)?.label ?? selectedMetric;
  }, [selectedMetric]);

  const chartTitle = useMemo(() => {
    const tokenList = selectedTickers.length > 0 ? selectedTickers.join(", ") : "Tokens";
    return `Historical ${metricLabel} Comparison - ${tokenList}`;
  }, [metricLabel, selectedTickers]);

  // Handlers
  const handleTickerChange = useCallback((value: string | string[]) => {
    const tickers = Array.isArray(value) ? value : [value];
    if (tickers.length > 0) {
      setSelectedTickers(tickers);
      onConfigChange?.({ tickers });
    }
  }, [onConfigChange]);

  const handleMetricChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const metric = e.target.value;
    setSelectedMetric(metric);
    onConfigChange?.({ metric });
  }, [onConfigChange]);

  const handleTimeRangeChange = useCallback((range: RangeKey) => {
    setTimeRange(range);
    onConfigChange?.({ timeframe: range });
  }, [onConfigChange]);

  const handleIndexToggle = useCallback((checked: boolean) => {
    setIndexTo100(checked);
    onConfigChange?.({ index_to_100: checked });
  }, [onConfigChange]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          {chartTitle}
        </span>
      </div>
      {/* Controls - styled like ChartControls */}
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-700/30">
        <div className="flex items-center gap-2">
          <CompactTokenSelector
            value={selectedTickers}
            onChange={handleTickerChange}
            multiple
            {...(availableTokens.length > 0 ? { tokens: availableTokens } : {})}
            placeholder="Tokens"
          />

          <select
            value={selectedMetric}
            onChange={handleMetricChange}
            className="select select-sm max-w-[140px] bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-xs"
          >
            {METRIC_OPTIONS.map(m => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          {/* Index toggle - only show if applicable */}
          {canIndex && (
            <div className="flex items-center bg-gray-800/50 border border-gray-700/50 rounded-lg p-1">
              <label className="flex items-center gap-1.5 cursor-pointer px-2 py-0.5 rounded-md text-[11px] font-medium transition-all duration-200 text-gray-400 hover:text-gray-200 hover:bg-gray-700/30">
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs"
                  checked={indexTo100}
                  onChange={(e) => handleIndexToggle(e.target.checked)}
                />
                <span>Index</span>
              </label>
            </div>
          )}

          <TimeRangePicker 
            options={['7d', '30d', '90d', '365d', 'All'] as const} 
            value={timeRange} 
            onChange={(v) => handleTimeRangeChange(v as RangeKey)} 
            theme="blue" 
          />
        </div>
      </div>

      {/* Chart container - measure actual height */}
      <div ref={chartContainerRef} className="flex-1 min-h-0">
        {isLoading ? (
          <div className={WIDGET_EMPTY}>
            Loading...
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-red-400 text-sm">
            {error}
          </div>
        ) : !hasData ? (
          <div className={WIDGET_EMPTY}>
            {selectedTickers.length === 0 ? "Select tokens to view chart" : "No data available"}
          </div>
        ) : (
          <MultiTokenChart 
            key={`chart-${selectedTickers.join(',')}-${selectedMetric}-${timeRange}-${chartHeight}`}
            dates={chartDates}
            series={chartSeries}
            height={chartHeight}
            chartType="line"
            yAxisFormat={yAxisFormat}
            showDataZoom={false}
            showToolbox={false}
            showLegend={selectedTickers.length > 1}
            metricId={selectedMetric}
          />
        )}
      </div>
    </div>
  );
};

export default TimeSeriesChartWidget;
